import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 * This should be called as early as possible in the application lifecycle
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_APP_VERSION || 'unknown';

  // Only initialize Sentry if DSN is provided
  if (!dsn || dsn === 'your-sentry-dsn-here') {
    console.warn('Sentry DSN not configured. Error tracking will be disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Session Replay (optional - can be enabled for better debugging)
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always record replays when errors occur
    
    // Integrations
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true, // Mask sensitive data
        blockAllMedia: true, // Block media for privacy
      }),
      Sentry.browserTracingIntegration(),
    ],

    // Filter out common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Network errors that are often not actionable
      'NetworkError',
      'Network request failed',
      // Third-party scripts
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // Chrome extensions
      'nonErrorPromiseRejection',
      'Chrome',
    ],

    // Filter out URLs that shouldn't be tracked
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Other browser extensions
      /safari-extension:/i,
    ],

    // Set user context (can be updated later when user logs in)
    beforeSend(event, hint) {
      // You can modify events before they're sent
      // For example, remove sensitive data
      return event;
    },
  });

  console.log('Sentry initialized successfully');
}

/**
 * Set user context for Sentry
 * Call this when a user logs in
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Clear user context
 * Call this when a user logs out
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category?: string, level?: Sentry.SeverityLevel, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    data,
  });
}
